import React, { Component } from "react";
import {
  PARAM_CHANCELLOR,
  PARAM_PLAYERS,
  PARAM_PRESIDENT,
  PLAYER_IDENTITY,
  SERVER_TIMEOUT,
} from "../constants";
import "../selectable.css";
import "./VotingPrompt.css";
import YesVote from "../assets/vote-yes.png";
import NoVote from "../assets/vote-no.png";
import portraits, { defaultPortrait } from "../assets";
import { GameState, Role, SendWSCommand, WSCommandType } from "../types";
import HitlerZoneWarning from "../effects/HitlerZoneWarning";
import TurnOrder from "../game-ui/TurnOrder";

type VotingPromptProps = {
  gameState: GameState;
  sendWSCommand: SendWSCommand;
  user: string;
};

type VotingPromptState = {
  selection?: string;
  waitingForServer: boolean;
};

type OfficialProps = {
  kind: "president" | "chancellor";
  name: string;
  icon: string | undefined;
  /** Extra tag under the name (a Fascist teammate, for example). */
  tag?: string;
  /** What this player learned by investigating them ("LIBERAL" or "FASCIST"), if they did. */
  checked?: string;
};

/** One of the two big name cards at the top of the prompt. */
function Official(props: OfficialProps) {
  const portrait =
    (props.icon && portraits[props.icon]) || portraits[defaultPortrait];
  return (
    <div className={"vp-official vp-official-" + props.kind}>
      {portrait ? (
        <img className="vp-avatar" src={portrait} alt="" />
      ) : (
        <span className="vp-avatar vp-avatar-empty">
          {props.name.charAt(0)}
        </span>
      )}
      <div className="vp-official-text">
        <div className="vp-role">
          {props.kind === "president" ? "President" : "Chancellor nominee"}
        </div>
        <div className="vp-name">{props.name}</div>
        {props.tag && <div className="vp-tag">{props.tag}</div>}
        {props.checked && (
          <div className={"vp-checked vp-checked-" + props.checked.toLowerCase()}>
            Checked: {props.checked === "FASCIST" ? "Fascist" : "Liberal"}
          </div>
        )}
      </div>
    </div>
  );
}

class VotingPrompt extends Component<VotingPromptProps, VotingPromptState> {
  timeoutID: NodeJS.Timeout | undefined;

  constructor(props: VotingPromptProps) {
    super(props);
    this.state = {
      selection: undefined,
      waitingForServer: false,
    };
    this.onButtonClick = this.onButtonClick.bind(this);
  }

  /**
   * Returns whether the chancellor's role should be shown on the card.
   * @return {boolean} Returns true iff the chancellor should be shown. This can happen if:
   *          - The player is fascist and the chancellor is fascist/hitler
   *          - The player is hitler, the chancellor is fascist, and there are 5-6 players.
   */
  shouldChancellorRoleBeShown() {
    let game = this.props.gameState;
    let userRole = game[PARAM_PLAYERS][this.props.user][PLAYER_IDENTITY];
    let chancellor = game[PARAM_CHANCELLOR];
    let chancellorRole = game[PARAM_PLAYERS][chancellor][PLAYER_IDENTITY];
    switch (userRole) {
      case Role.LIBERAL:
        return false;
      case Role.FASCIST:
        if (chancellorRole === Role.HITLER || chancellorRole === Role.FASCIST) {
          return true;
        }
        break;
      case Role.HITLER:
        if (chancellorRole === Role.FASCIST && game.playerOrder.length <= 6) {
          return true;
        }
        break;
      default:
    }
    return false;
  }

  /**
   * Called when the confirm button is clicked.
   * @effects Attempts to send the server a command with the player's vote, and locks access to the button
   *          for {@code SERVER_TIMEOUT} ms.
   */
  onButtonClick() {
    // Lock the button so that it can't be pressed multiple times.
    this.timeoutID = setTimeout(() => {
      this.setState({ waitingForServer: false });
    }, SERVER_TIMEOUT);
    this.setState({ waitingForServer: true });

    // Contact the server using provided method.
    this.props.sendWSCommand({
      command: WSCommandType.REGISTER_VOTE,
      vote: this.state.selection === "yes",
    });
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutID);
  }

  render() {
    const game = this.props.gameState;
    const chancellorName = game[PARAM_CHANCELLOR];
    const presidentName = game[PARAM_PRESIDENT];
    const chancellorRole = game[PARAM_PLAYERS][chancellorName][PLAYER_IDENTITY];
    const roleTag = this.shouldChancellorRoleBeShown()
      ? chancellorRole === Role.HITLER
        ? "Hitler"
        : "Fascist"
      : undefined;

    return (
      <div className="vp">
        <div className="vp-info">
          <div className="vp-kicker">Vote on this government</div>

          <div className="vp-gov">
            <Official
              kind="president"
              name={presidentName}
              icon={game.icon[presidentName]}
              checked={game.players[presidentName]?.investigated ? game.investigations?.[presidentName] : undefined}
            />
            <Official
              kind="chancellor"
              name={chancellorName}
              icon={game.icon[chancellorName]}
              tag={roleTag}
              checked={game.players[chancellorName]?.investigated ? game.investigations?.[chancellorName] : undefined}
            />
          </div>

          <TurnOrder game={game} nominee={chancellorName} />

          {/* These are two optional warnings that appear when player decisions are extra critical,
            such as if fascists can win the game or if the voting tracker will hit the end. */}
          {game.fascistPolicies >= 3 && (
            <HitlerZoneWarning>
              Fascists win instantly if Hitler is voted in as Chancellor. Vote
              carefully!
            </HitlerZoneWarning>
          )}
          {game.electionTracker === 2 && (
            <div className="vp-tracker">
              If this vote fails, the top policy of the draw pile is enacted
              immediately.
            </div>
          )}
        </div>

        <div className="vp-ballot">
          <div className="vp-cards">
            <img
              className={
                "vp-card selectable " +
                (this.state.selection === "yes" ? "selected " : "")
              }
              src={YesVote}
              alt={"Ja! (Yes)"}
              onClick={() => this.setState({ selection: "yes" })}
            />
            <img
              className={
                "vp-card selectable " +
                (this.state.selection === "no" ? "selected " : "")
              }
              src={NoVote}
              alt={"Nein (No)"}
              onClick={() => this.setState({ selection: "no" })}
            />
          </div>

          <div className="vp-note">
            The vote passes if more than half of the votes are Ja.
          </div>

          <div className="vp-actions">
            <button
              id={"prompt-button"}
              className="gold-button vp-confirm"
              disabled={
                this.state.selection === undefined ||
                this.state.waitingForServer
              }
              onClick={this.onButtonClick}
            >
              <span className="gold-label">CONFIRM</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default VotingPrompt;
